import type { AxiosError } from 'axios';
import type {
  CarrierZoneInfo,
  PartnerRateTable,
  ShiptimeCarrierLabel,
  ShiptimeCarrierSelectionSummary,
  ShiptimeShipmentParameters,
} from '@shipium-corp/shipium-ts-types';

import type { Contact, FulfillmentCenterConfig } from '../FulfillmentConfigService/types';
import type { Address } from '../../../types';
import type { FiliteredServiceMethod, Shipment, Weight } from '../ShiptimeService/types';
import type { PrintToolFormSections } from '../../../components/shipment/PrintLabelContainer';

export type PackageType = 'box' | 'envelope' | 'flat_pack';
export type DimensionUnit = 'in' | 'cm';
export type WeightUnit = 'lb' | 'kg';
export type IncotermDefault = 'DDP' | 'DDU' | 'DAP' | 'DPU' | 'FCA';
export type ManifestType = 'batch' | 'batch_asynchronous' | 'single' | 'none';
export type DestinationType = 'all' | 'commercial' | 'residential';
export type TransportationType = 'all' | 'air' | 'ground';
export type CarrierStatus = 'AVAILABLE' | 'AVAILABLE_SHIPIUM' | 'UNAVAILABLE';

export const CarrierStatuses: Record<string, CarrierStatus> = {
  AVAILABLE: 'AVAILABLE',
  UNAVAILABLE: 'UNAVAILABLE',
  AVAILABLE_SHIPIUM: 'AVAILABLE_SHIPIUM',
};

export type SupportedDeliverySignatureType =
  | 'signature'
  | 'resident_signature'
  | 'adult_signature'
  | 'adult_resident_signature';

export type SupportedWeekendDeliveryType =
  | 'none'
  | 'all_zip_codes'
  | 'select_zip_codes'
  | 'all_zip_codes_no_cost'
  | 'select_zip_codes_no_cost';

export type RateTableType = 'defined_ranges' | 'units_and_sub_units' | 'sub_units' | 'units' | 'flat_rate';

export type ServiceLevel =
  | 'same_day'
  | 'next_day'
  | 'priority_overnight'
  | 'overnight'
  | 'two_day_morning'
  | 'two_day'
  | 'three_day'
  | 'standard_plus'
  | 'standard'
  | 'international_24_hour'
  | 'international_one_to_three_day_early'
  | 'international_one_to_three_day'
  | 'international_two_to_five_day'
  | 'international_two_to_ten_day'
  | 'international_one_to_two_week'
  | 'international_one_to_four_week'
  | 'unknown';

export type CarrierZoneKeyType =
  | 'postal_to_postal'
  | 'postal3_to_postal'
  | 'postal3_to_postal1'
  | 'any_country_to_any_country'
  | 'any_country_to_postal'
  | 'state_group_to_any_country'
  | 'postal3_to_any_country'
  | 'any_US_origin_to_postal_group'
  | 'any_US_origin_to_service_method_postal_group'
  | 'state_group_to_postal_group'
  | 'unknown';

export type HundredweightCapability = 'hundredweight_only' | 'not_limited' | 'never_hundredweight';

export type ThresholdType =
  | 'max'
  | 'max_average_volume'
  | 'max_combined'
  | 'max_length'
  | 'max_sum'
  | 'max_volume'
  | 'min'
  | 'min_all'
  | 'min_combined'
  | 'max_length_divided_height'
  | 'min_volume'
  | 'min_width_max_length'
  | 'min_width_max_height'
  | 'min_length_divided_height'
  | 'match'
  | 'floor';

export type CarrierSurchargeId =
  | 'delivery'
  | 'extended_delivery'
  | 'fuel_air_domestic'
  | 'fuel_air_international_export'
  | 'fuel_air_international_import'
  | 'fuel_ground'
  | 'peak'
  | 'residential_ground'
  | 'delivery_commercial_ground'
  | 'delivery_residential_ground'
  | 'extended_delivery_commercial_ground'
  | 'extended_delivery_residential_ground'
  | 'saturday_delivery'
  | 'signature'
  | 'signature_resident'
  | 'signature_adult'
  | 'alaska_delivery_commercial_air'
  | 'alaska_delivery_commercial_ground'
  | 'alaska_delivery_residential_air'
  | 'alaska_delivery_residential_ground'
  | 'hawaii_delivery_commercial_air'
  | 'hawaii_delivery_commercial_ground'
  | 'hawaii_delivery_residential_air'
  | 'hawaii_delivery_residential_ground'
  | 'international_delivery_area'
  | 'international_extended_delivery_area'
  | 'delivery_commercial_air'
  | 'delivery_residential_air'
  | 'extended_delivery_commercial_air'
  | 'extended_delivery_residential_air'
  | 'residential_air'
  | 'signature_adult_resident'
  | 'incentive';

export interface FedexHubId {
  code: string;
  hubId: string;
  location: string;
}

export interface LabelAugmentation {
  fulfillmentCenters?: FulfillmentCenterConfig[];
  fulfillmentCenterIds?: string[];
  serviceMethodId?: string;
  serviceMethodName?: string;
  name: string;
  zpl: string;
  labelImage?: string;
  injectionPosition?: string;
  shipmentTagToMatch: string;
}
export interface UpdateLabelAugmentationsRequest {
  carrierId: string;
  carrierAccountId: string;
  serviceMethodId: string;
  labelAugmentations: LabelAugmentation[];
  partnerId: string;
}

export interface CredentialDefintion {
  credentialStructureId?: string;
  credentials: Record<string, string>;
}
export interface CreateCarrierAccountRequest {
  active?: boolean;
  carrierId: string;
  credentialDefinition: CredentialDefintion;
  fulfillmentCenterIds: string[];
  fulfillmentContexts?: string[];
  hazmatLimitedQuantityExempt: boolean;
  name: string;
  contact?: Contact;
  shipperAddress?: Address;
  peakSurchargeEligible?: boolean;
  expectedShippingVolumeOveragePercentage?: number;
}

export interface RateFileOptions {
  zones: string[];
  weights: number[] | string[];
  fileData: any;
}

export interface CarrierTestFileOptions {
  fileData: any;
  shipmentEffectiveDate: string;
}

export interface ServiceMethodParamCalendar {
  active?: boolean;
  startDateTime: string;
  endDateTime: string;
  name: string;
  serviceMethodParamUrn: string;
  serviceMethodParamId: string;
}

export interface CredentialProperty {
  credentialId: string;
  name: string;
  optional?: boolean;
}

export interface Zone {
  zoneId: string;
  name: string;
}

export interface MaxCustomsInfoValue {
  currency: string;
  maxValue: number;
}

export interface ExtendedDimensions {
  dimensionGroupId: string;
  dimensions: DimensionThreshold[];
}

export interface ServiceMethodRestrictions {
  dimensions: DimensionThreshold[];
  extendedDimensions?: ExtendedDimensions[];
  weights: WeightRestriction[];
  maxCustomsInfoValue?: MaxCustomsInfoValue[];
}

export interface HazmatInfo {
  dangerousGoods: {
    supportStrategy: string;
  };
  limitedQuantity: {
    countries: string[];
    exclusionsByPostalCode: Record<string, string[]>;
    exclusionsByRegion: Record<string, string[]>;
    supportStrategy: string;
  };
}

export interface WeightFloor {
  floorWeight: number;
  dimensionThresholds: DimensionThreshold[];
}

export interface DimensionalWeight {
  volumeThreshold: number;
  volumeWeightDivisor: number;
  nonBoxDimWeightMultiplier: number;
  weightFloor: number;
}
export interface RateTableRangeConfigurationRange {
  endInclusive: number;
  startInclusive: number;
  increment: number;
}
export interface RateTableRangeConfiguration {
  values?: number[];
  weightUnit: string;
  ranges?: RateTableRangeConfigurationRange[];
}

export interface OriginBasedSupportedRegionsByCountry {
  //country codes
  exclude?: string;
  include?: string;
}

export interface ServiceMethodOverrides {
  carrierIdForTrackingRegistration?: string;
  parentServiceMethodId?: string;
  tntParentServiceMethodId?: string;
  zoneParentServiceMethodId?: string;
}

export interface PoBoxDeliveryExclusionByCountry {
  countryCode?: string;
  allowedRegions?: string[];
}
export interface OverweightRating {
  dimensions: DimensionThreshold[];
}

export interface RestrictionsConditions {
  applicableCountries?: string[];
  excludedCountries?: string[];
  dimensionConditions?: DimensionThreshold[];
}

export interface DimensionThreshold {
  combined: number;
  conditions?: RestrictionsConditions[];
  dimensionUnit: DimensionUnit;
  height: number;
  length: number;
  packageType: PackageType;
  strategies?: any; //not well defined in carrier service swagger
  thresholdType: ThresholdType;
  width: number;
}

export interface WeightFilterThreshold {
  packageType: string | null;
  thresholdType: string;
  weight: number;
  weightUnit: string;
}

export interface ServiceMethodParam {
  serviceMethodParamId: string;
  serviceMethodParamUrn: string;
  serviceMethodUrn: string;
  name: string;
  startDateTime: string;
  endDateTime: string;
  rateTable?: PartnerRateTable;
  active: boolean;
  negotiatedSurcharges?: CarrierSurchargeInfo[];
  uncosted?: boolean;
}

export interface FetchServiceMethodParmOptions {
  partnerId: string;
  carrierAccountId: string;
  serviceMethodId: string;
  serviceMethodParamId: string;
}

export interface CarrierServiceMethodOptions {
  serviceMethodId?: string;
  partnerId: string;
  carrierAccountId: string;
  carrierId: string;
  carrierServiceMethodId?: string;
  carrierAccountUrn: string;
  destinationType: string;
  name: string;
  testMode?: boolean;
  active: boolean;
  archive?: boolean;
  volumeThreshold?: number;
  volumeWeightDivisor?: number;
  credentialDefinition?: CredentialDefintion;
}

export interface CreateServiceMethodParamOptions {
  startDateTime: string;
  endDateTime: string;
  rateTable?: PartnerRateTable;
  serviceMethodId: string;
  serviceMethodUrn: string;
  partnerId: string;
  carrierAccountId: string;
  name: string;
  active?: boolean;
  negotiatedSurcharges?: CarrierSurchargeInfo[];
  uncosted?: boolean;
}

export interface WeightRestriction {
  packageType: PackageType;
  weightUnit: WeightUnit;
  thresholdType: 'max' | 'min' | 'floor';
  weight: number;
}

export interface OrderItemQuantities {
  partnerOrderId?: string;
  productDetails?: string[];
  productId: string;
  quantity: number;
  hazmat?: boolean;
  hazmatInfo?: HazmatParams;
}

export interface LabelEntry {
  key: string;
  value: string;
}

export interface HazmatParams {
  hazmat?: boolean;
  category?: string;
  containerType?: string;
  hazardClass?: string;
  packingGroup?: string;
  hazmatId?: string;
  properShippingName?: string;
  quantity?: number;
  quantityType?: string;
  quantityUnits?: string;
  subsidiaryClasses?: string[];
}
export interface CustomsItem {
  commodityDescription: string;
  countryOfManufacture: string;
  customsValue: number;
  customsValueCurrency: string;
  quantity: number;
  quantityUnitOfMeasurement: string;
  harmonizedTariffNumber: number;
  customsWeight: Weight;
}
export interface CustomsInfo {
  customsDescription: string;
  customsItems: CustomsItem[];
  totalCustomsValue: number;
  totalCustomsValueCurrency: string;
}

export interface RateCalculationTestRequest {
  hazmatParams?: HazmatParams;
  purchaseOrderIdentifier?: string;
  businessDaysOfTransit?: number;
  shipOption?: string;
  deliverySignatureOption?: ShiptimeShipmentParameters['deliverySignatureOption'];
  desiredDeliveryDate?: string;
  fulfillmentContext?: string;
  saturdayDelivery?: boolean;
  referenceIdentifier?: string;
  orderItemQuantities?: OrderItemQuantities[];
  generateLabel?: boolean;
  shipmentTags?: string[];
  testMode?: boolean;
  carrierServiceMethodAllowList?: string[];
  customLabelEntries?: Record<string, string>;
  calculateRatesFromAllFCs?: boolean;
  packagingMaterial?: PackageType;
  partnerId?: string;
  customsInfo?: CustomsInfo;
  unsupportedProductDetails?: string[];
  packagelinearDimensions?: {
    height: number;
    length: number;
    linearUnit: DimensionUnit;
    width: number;
  };
  packageWeight?: {
    weight?: number;
    weightUnit?: WeightUnit;
  };
  partnerShipmentId?: string;
  shipmentSourceAddress?: Address;
  shipmentSourceAddresses?: Address[];
  shipmentDestinationAddress?: Address;
  shipmentEffectiveDate?: string;
  shipDate?: string;
  selectedSubForm?: PrintToolFormSections;
  selectedTab?: 'form' | 'bulk';
  preventVoid?: boolean;
  megaProcessVersion?: string;
}

// may be missing in partial shipments, children included
export interface PackagingType {
  packagingMaterial?: PackageType;
  packagingSizeName?: string;
  packagingTypeId?: string | null;
  linearDimensions?: LinearDimensions;
  packagingWeight?: {
    weight?: number;
    weightUnit?: WeightUnit;
  };
}

export interface LabelDocument {
  labelFormat: string;
  labelUrl: string;
  labelExpiration: string;
  labelProperties: {
    dpi: number;
    height: number;
    labelOrientation: string;
    linearUnits: string;
    width: number;
  };
  labelImage: {
    imageEncoding: string;
    imageContents: string;
  };
}
//TODO: is this from shiptime service?
export interface MegaShipmentProcessCreateResponse {
  labelVoided: boolean;
  shipiumShipmentId: string;
  orderedDateTime: string;
  shippedDateTime: string;
  deliveredDateTime?: string;
  carrierName?: string;
  carrierTrackingId?: string;
  orderItemQuantities: OrderItemQuantities[];
  destinationAddress?: Address; // may be missing in partial shipments, children included
  shipOption?: string;
  shipFromAddress: Address;
  fulfillmentCenterId: string;
  packagingType?: PackagingType; // may be missing in partial shipments, children included
  totalWeight?: {
    // may be missing in partial shipments, children included
    weight?: number;
    weightUnit?: WeightUnit;
  };
  shipmentTags: string[];
  carrierSelection: {
    //possibly ShipmentCarrierSelection
    // missing
    //shipmentShipDate
    //carrierSelectionExpiration
    status: ShiptimeCarrierSelectionSummary['status'];
    carrierSelectionDateTime: string;
    shipmentShipDate: string;
    carrierSelectionId: string;
    carrierSelectionExpiration: string;
    shipiumShipmentId: string;
    partnerShipmentId?: string;
    partnerId: string;
    carrierAccountId: string;
    serviceMethodUrn: string;
    carrier: string;
    serviceMethodName: string;
    totalCost: number;
  };
  carrierLabel: ShiptimeCarrierLabel;
  requestBody?: any;
  responseBody?: any;
  duration?: number;
  partnerThirdPartyBillingSetId?: string;
  forceThirdPartyBilling?: boolean;
  requestThirdPartyBillingSetId?: string;
  // this will be included in a partial shipment
  // it will indicate the missing fields
  missingRequiredLabelFields?: string[];
}

export interface RateCalculationTestResponseLineItem {
  name: string;
  rate: number;
  surchargeType: string;
}

export interface LinearDimensions {
  height: number;
  length: number;
  linearUnit: DimensionUnit;
  width: number;
}

export interface ServiceMethodSummary {
  partnerId: string;
  carrierAccountId: string;
  serviceMethodUrn: string;
  carrier: string;
  carrierServiceName: string;
  serviceMethodName: string;
  destinationType: string;
  transportationType: string;
  uspsPoBoxDeliverySupported: boolean;
}

export interface CalculatedRateSummary {
  actualWeight: number;
  asOfTime: string;
  billableWeight: number;
  carrierAccountId: string;
  carrierId: string;
  createdAt: string;
  destinationAddressType: string;
  destinationCountryCode: string;
  destinationPostalCode: string;
  dimWeight?: number | null;
  lineItems: RateCalculationTestResponseLineItem[];
  linearDimensions?: LinearDimensions;
  packagingMaterial: string;
  partnerId: string;
  physicalWeight: number;
  rate: number;
  rateCurrency: string;
  rateResultType: string;
  serviceMethodUrn: string;
  weightUnit: string;
  zoneId: string;
  serviceMethodIdentifier?: string;
}

export interface RateCalculationTestResponseDetails {
  requestParams: {
    currencyCode?: string;
    shipmentShipDate?: string;
    packagingType?: {
      packagingMaterial?: PackageType;
      packagingSizeName?: string;
      packagingTypeId?: string | null;
      linearDimensions?: LinearDimensions;
      packagingWeight?: {
        weight?: number;
        weightUnit?: WeightUnit;
      };
    };
  };
  filteredServiceMethods?: FiliteredServiceMethod[];
  availableServiceMethods: ServiceMethodSummary[];
  serviceMethodsAfterFiltering: ServiceMethodSummary[];
  zonesByCarrier: Record<string, string[]>;
  calculatedRates: CalculatedRateSummary[];
}

export interface RateCalculationTestResponse {
  labelVoided?: boolean;
  shipiumShipmentId: string;
  partnerShipmentId?: string;
  ruleSetUrn: string;
  ruleSetName: string;
  carrierLabel?: ShiptimeCarrierLabel;
  carrierDecision: {
    partnerId: string;
    status: 'no_decision' | 'success' | 'uncosted' | string;
    statusDetails?: string;
    carrierAccountId: string;
    serviceMethodUrn: string;
    carrier: string;
    serviceMethodName: string;
    carrierServiceName: string;
    carrierEstimatedPriceDetails: {
      totalCost: number;
      currencyCode?: string;
      shippingCost: number;
      insuranceCost: number;
      accessorials: number;
      duty: number;
      taxes: number;
    };
    details: RateCalculationTestResponseDetails;
  };
  carrierSelectionDateTime: string;
  shipmentShipDate?: string;
  carrierSelectionId: string;
  carrierSelectionExpiration: string;
  requestBody?: any;
  responseBody?: any;
  duration?: number;
  carrierSelection?: ShiptimeCarrierSelectionSummary;
  partnerThirdPartyBillingSetId?: string;
  forceThirdPartyBilling?: boolean;
  requestThirdPartyBillingSetId?: string;
}

export interface RateCalculationTestRecord {
  request?: RateCalculationTestRequest;
  response?: RateCalculationTestResponse;
  responses?: CarrierRateSelectionResponseMap;
  expectedCost?: number;
  rowIndex: number;
  errorMessage?: string;
  warningMessage?: string;
  promise?: Promise<RateCalculationTestResponse>;
  promises?: Promise<CarrierRateSelectionResponseMap>;
}

export interface CarrierSurchargeInfo {
  modifierType: 'credit' | 'percentage' | 'replacement' | 'replacement_percentage' | 'fixed_percentage_discount';
  modifierValue: number;
  name: string;
  surchargeId: CarrierSurchargeId | string;
}

export type CarrierRateSelectionResponse = {
  status: number;
  shipment?: Shipment;
  carrierSelection?: RateCalculationTestResponse;
  error?: AxiosError | Error;
  key: string;
};

export interface TestLabel {
  carrier: string;
  carrierServiceMethodId: string;
  zpl: string;
  error: boolean;
}

export interface ExpiringRate {
  serviceMethodName: string;
  rateTableName: string;
  carrierAccountId: string;
  serviceMethodId: string;
  expiresOn: string;
}

export type CarrierRateSelectionResponseMap = Record<string, CarrierRateSelectionResponse>;

export interface ZoneDiffAddedOrRemoved {
  key: string;
  zones: string[];
}

export interface ZoneDiffModified {
  key: string;
  before: string[];
  after: string[];
}

export interface ZoneDiff {
  added?: ZoneDiffAddedOrRemoved[];
  removed?: ZoneDiffAddedOrRemoved[];
  modified?: ZoneDiffModified[];
}

export interface ZonePreviewResponse {
  diff: ZoneDiff;
  metadata: Record<string, string>;
  previewId: string;
}

export interface ZoneApplyPreviewRequest {
  carrierId: string;
  fromCountryCode: string;
  previewId: string;
}

export interface UsedCarrierMap {
  [carrier: string]: {
    methods: {
      [method: string]: { name: string };
    };
    name: string;
  };
}

export interface UpsReadyOptions {
  oauthRedirectUrl: string;
}

export interface UpsReadyAuthResponse {
  id: number;
  userId: string;
  partnerId: string;
  authCode: string;
  refreshToken: string;
  createdAtTimestamp: string;
}

export interface SearchServiceMethodsParams {
  serviceMethodType?: 'simulation_sell' | 'standard' | 'returns';
  dataSource?: 'data' | 'yaml';
}

export interface FetchCarrierZonesParams {
  carriers: string[];
  showInternational: boolean;
}

export interface CarrierSummary {
  carrierId: string;
  name: string;
  status: 'UNAVAILABLE' | 'AVAILABLE_SHIPIUM' | 'AVAILABLE' | undefined;
  serviceMethods: CarrierSummaryServiceMethodSummary[];
}

export interface CarrierSummaryServiceMethodSummary {
  name: string;
  configured: boolean;
  serviceMethodId: string;
}

export interface CarrierZoneSummary {
  carrierId: string;
  name: string;
  zones: CarrierZoneInfo[];
}

export interface FetchCurrencyConversionRatesParams {
  carrierId?: string;
  baseCurrencyCode: string;
  asOfDate?: string;
}

export interface FetchCurrencyConversionParams {
  carrierId?: string;
  fromCurrencyCode: string;
  toCurrencyCode: string;
  monetaryAmount: number;
  asOfDate?: string;
}
